/**
 * REGISTRY DELLE CONFIGURAZIONI DI AZIONE (blocco ALLORA) — codice azione → form.
 *
 * Ordine di ricerca della modale, in tre passi:
 *  1. il form scritto a mano per il codice: prima quelli dei plugin ABILITATI
 *     (`PluginManifest.automationActionForms`), poi quelli del core (la mappa qui sotto);
 *  2. altrimenti i parametri semplici che l'azione dichiara nel catalogo (`params`, `getParams` lato
 *     Java), resi da `DeclaredParamsActionConfig` con lo stesso `ParamField` del QUANDO;
 *  3. altrimenti «nessun parametro».
 * La mappa serve a ciò che un descrittore di parametri scalari non copre: due tendine collegate per
 * aggiornare un campo, un corpo mail coi placeholder, un destinatario su un'entità collegata.
 *
 * Questa cartella contiene **un form per azione** più questo indice, che ne è la faccia pubblica:
 * chi sta fuori usa solo `useActionConfigForm`, i form non li raggiunge nessuno direttamente. Fuori
 * resta la modale che li ospita, che non è di un'azione ma di tutte. Stessa separazione del backend,
 * dove i provider stanno in `internal/catalog/actions/` e chi li usa li risolve dal registro OSGi
 * senza importarli.
 *
 * Questo file è l'unico punto del frontend che nomina i codici azione. Chi mostra la configurazione
 * chiede e basta, quindi non cresce quando le azioni aumentano — a differenza di una catena di
 * `if (code === …)`, che andrebbe replicata ovunque una config si mostri (la modale, il dettaglio
 * della riga, il riepilogo). Un'azione con soli parametri semplici tocca solo Java (il suo
 * `@Component` con `getParams`); una con un form proprio anche questa mappa.
 *
 * È lo specchio del lookup che il backend fa già: `_validateThen` prende il `code` dal payload,
 * risolve il provider fra i servizi OSGi registrati e gli delega la config, senza conoscerne
 * nessuno. Qui il registro dei servizi è una mappa statica, il resto è identico. Stessa forma anche
 * di `automationIcons.ts`, che risolve il token icona allo stesso modo.
 *
 * **Codice senza voce = nessun form scritto a mano.** Non è un errore: l'azione può dichiarare i
 * propri parametri, non averne (il catalogo porta `hasConfiguration`, e la modale dice "nessun
 * parametro" invece di lasciare il vuoto sotto il titolo), o essere di un modulo cliente che il
 * core non conosce — e non deve rompere la modale.
 */

import { useMemo } from 'react';
import type { ComponentType } from 'react';
import type { ActionConfigProps } from '@crm/contracts';
import { useEnabledPlugins } from '../../../plugin/useEnabledPlugins';
import CreateTaskActionConfig from './CreateTaskActionConfig';
import DeclaredParamsActionConfig from './DeclaredParamsActionConfig';
import SendEmailActionConfig from './SendEmailActionConfig';
import UpdateFieldActionConfig from './UpdateFieldActionConfig';

export type { ActionConfigProps };

const ACTION_CONFIG_FORM: Record<string, ComponentType<ActionConfigProps>> = {
  'crm.create-task': CreateTaskActionConfig,
  'crm.send-email': SendEmailActionConfig,
  'crm.update-field': UpdateFieldActionConfig,
};

/**
 * Sceglie il form per un codice: plugin abilitati (nell'ordine di registrazione, il primo vince),
 * poi la mappa del core. Pura, per poterla provare senza React.
 */
export function resolveActionConfigForm(
  code: string,
  pluginForms: ReadonlyArray<{
    pluginId: string;
    forms?: Record<string, ComponentType<ActionConfigProps>>;
  }>,
): ComponentType<ActionConfigProps> | null {
  const hits = pluginForms.filter((p) => p.forms?.[code]);
  if (import.meta.env.DEV && hits.length > 1) {
    console.warn(
      `Form di azione «${code}» registrato da più plugin (${hits.map((h) => h.pluginId).join(', ')}): vale il primo.`,
    );
  }
  return hits[0]?.forms?.[code] ?? ACTION_CONFIG_FORM[code] ?? null;
}

/** Il form di configurazione di un'azione, o `null` se quell'azione non ne ha uno. */
export function useActionConfigForm(code: string | null | undefined): ComponentType<ActionConfigProps> | null {
  const enabledPlugins = useEnabledPlugins();
  return useMemo(
    () =>
      code
        ? resolveActionConfigForm(
            code,
            enabledPlugins.map((p) => ({ pluginId: p.id, forms: p.automationActionForms })),
          )
        : null,
    [enabledPlugins, code],
  );
}

/** Il form generico dei parametri che un'azione dichiara: per chi non ha un form nella mappa. */
export { DeclaredParamsActionConfig };
